import React, {useEffect, useState} from 'react'
import {Avatar, Badge, Box, CircularProgress, Divider, Stack, Tooltip, Typography} from '@mui/material'
import {useTheme} from '@mui/material/styles'
import PersonIcon from '@mui/icons-material/Person'
import EventIcon from '@mui/icons-material/Event'
import Icon from '@mdi/react'
import {mdiDoctor, mdiDomain, mdiNavigation} from '@mdi/js'
import {useAppSelector} from 'src/hooks'
import {STATUS} from 'src/constants/constants'
import apiClient from 'src/services/apiClient'
import Card from 'src/components-new/layout/Card'
import EmptyState from 'src/components-new/layout/EmptyState'
import InlineMessage from 'src/components-new/feedback/InlineMessage'
import {NEUTRAL_SCALE, SEMANTIC} from 'src/theme-new/tokens'

const timeFormat = new Intl.DateTimeFormat('it', {hour: 'numeric', minute: 'numeric'})
const dateFormat = new Intl.DateTimeFormat('it', {weekday: 'short', day: '2-digit', month: 'short'})
const yearFormat = new Intl.DateTimeFormat('it', {year: 'numeric'})

interface PersonalEvent {
  startDate: string
  title: string
}

// Same mapping as legacy `parseStates` in src/pages/patientSummary/PatientSummary.tsx and
// `parseStateLabel`/`getStateColor` in src/pages-new/patient/PatientDetailNew.tsx — kept
// identical here for consistency across the patient-state displays in the new UI.
function parseStateLabel(state: string | undefined): string {
  switch (state) {
    case 'TO_ACTIVE': return 'Da attivare'
    case 'ACTIVATING': return 'In attivazione'
    case 'ACTIVE': return 'Attivo'
    case 'ACTIVE_DELIVERY': return 'Attivo delivery'
    case 'SUSPENDED': return 'Sospeso'
    case 'WAITING_TO_EXIT': return 'In attesa di uscita'
    case 'EXITED': return 'Drop-out'
    default: return '-'
  }
}

function getStateColor(state: string | undefined): 'success' | 'warning' | 'error' | 'info' | 'default' {
  if (!state) return 'default'
  if (state === 'ACTIVE' || state === 'ACTIVE_DELIVERY') return 'success'
  if (state === 'SUSPENDED') return 'warning'
  if (state === 'EXITED') return 'error'
  return 'default'
}

// Solid dot color for the avatar status badge — distinct from StatusPill's pastel
// `.light` background, a small dot needs the vivid `.main` tone to read at a glance.
// 'default' (TO_ACTIVE / ACTIVATING / WAITING_TO_EXIT) has no SEMANTIC entry, so it
// falls back to a neutral gray — the tooltip is what actually disambiguates those three.
function getStateDotColor(state: string | undefined): string {
  const color = getStateColor(state)
  return color === 'default' ? NEUTRAL_SCALE[300] : SEMANTIC[color].main
}

interface InfoBlockProps {
  icon: React.ReactNode
  label: string
  value: string
  href?: string
}

function InfoBlock({icon, label, value, href}: InfoBlockProps): JSX.Element {
  return (
    <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{minWidth: 0, flexShrink: 0}}>
      <Box
        sx={{
          width: 40,
          height: 40,
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: NEUTRAL_SCALE[100],
          color: NEUTRAL_SCALE[700],
          flexShrink: 0,
        }}
      >
        {icon}
      </Box>
      <Box sx={{minWidth: 0, maxWidth: 230}}>
        <Typography variant="caption" sx={{color: NEUTRAL_SCALE[500], display: 'block'}}>
          {label}
        </Typography>
        {href ? (
          <Typography
            variant="body2"
            component="a"
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              display: 'block',
              fontWeight: 600,
              color: 'primary.main',
              textDecoration: 'none',
              '&:hover': {textDecoration: 'underline'},
            }}
          >
            {value}
          </Typography>
        ) : (
          <Typography variant="body2" sx={{fontWeight: 600}}>
            {value}
          </Typography>
        )}
      </Box>
    </Stack>
  )
}

// Timeline geometry shared by the event rows and the "Oggi" divider.
const DATE_COL_WIDTH = 88
const COL_GAP = 16
const DOT_SIZE = 10
const ROW_PADDING_Y = 12
const FIRST_LINE_HEIGHT = 20
// Horizontal center of the dot column, measured from the left edge of the list.
const DOT_CENTER_X = DATE_COL_WIDTH + COL_GAP + DOT_SIZE / 2

function TodayDivider(): JSX.Element {
  // The container is twice the dot-center offset wide and the two dividers flex equally,
  // so the "OGGI" label is centered exactly above the dot column without touching the lines.
  return (
    <Box sx={{display: 'flex', alignItems: 'center', gap: 1.5, py: 1, width: DOT_CENTER_X * 2}}>
      <Divider sx={{flex: 1}} />
      <Typography
        variant="caption"
        sx={{
          fontWeight: 700,
          color: NEUTRAL_SCALE[700],
          letterSpacing: 0.5,
          flexShrink: 0,
          whiteSpace: 'nowrap',
        }}
      >
        OGGI
      </Typography>
      <Divider sx={{flex: 1}} />
    </Box>
  )
}

export default function SummaryPatientNew(): JSX.Element {
  const theme = useTheme()
  const userInfo = useAppSelector(state => state.userReducer.data)

  const [events, setEvents] = useState<PersonalEvent[]>([])
  const [status, setStatus] = useState<string>()
  const [avatarImageSrc, setAvatarImageSrc] = useState<string>()

  useEffect(() => {
    apiClient.getPersonalEvents(setStatus)
      .then((fetchedData: PersonalEvent[]) => setEvents(fetchedData ?? []))
      .catch(() => setStatus(STATUS.REJECTED))
  }, [])

  useEffect(() => {
    if (!userInfo?.avatarId) return
    apiClient.getByIdAvatars(userInfo.avatarId)
      .then((fetchedData: any) => setAvatarImageSrc(fetchedData?.imageBase64))
      .catch(() => undefined)
  }, [userInfo?.avatarId])

  const medicalCenter = userInfo?.medicalCenters?.[0]
  const medicalCenterAddress = [medicalCenter?.address, medicalCenter?.streetNumber]
    .filter(Boolean)
    .join(', ')
  const medicalCenterFullAddress = [medicalCenterAddress, medicalCenter?.city].filter(Boolean).join(', ')
  const medicalCenterMapsUrl = medicalCenterFullAddress ?
    `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(medicalCenterFullAddress)}` :
    undefined

  // Same index computation as legacy PatientSummary.tsx: the last event whose startDate
  // (ISO string, safe to compare lexicographically) is in the future — the "Oggi"
  // separator is then rendered right after it, splitting past events from upcoming ones.
  const appointmentIndexAfterToday = events.findLastIndex(
    (event: PersonalEvent) => event.startDate > new Date().toISOString(),
  )

  return (
    <Box>
      {status === STATUS.REJECTED && (
        <InlineMessage severity="error" onClose={() => setStatus(STATUS.FULFILLED)} sx={{mb: 2}}>
          Impossibile recuperare i dati.
        </InlineMessage>
      )}

      <Card sx={{mb: 2}}>
        <Stack direction="row" spacing={2} alignItems="center">
          <Tooltip title={`Stato: ${parseStateLabel(userInfo?.state)}`}>
            <Badge
              overlap="circular"
              anchorOrigin={{vertical: 'bottom', horizontal: 'right'}}
              sx={{
                '& .MuiBadge-badge': {
                  width: 16,
                  height: 16,
                  borderRadius: '50%',
                  backgroundColor: getStateDotColor(userInfo?.state),
                  border: '2px solid #fff',
                },
              }}
              variant="dot"
            >
              <Avatar
                src={avatarImageSrc ? `data:image/png;base64,${avatarImageSrc}` : undefined}
                sx={{width: 80, height: 80, backgroundColor: NEUTRAL_SCALE[100], color: NEUTRAL_SCALE[500]}}
              >
                {!avatarImageSrc && <PersonIcon sx={{fontSize: 40}} />}
              </Avatar>
            </Badge>
          </Tooltip>

          <Box>
            <Typography variant="h1" sx={{fontSize: 24, fontWeight: 600}}>
              {userInfo?.avatarNickName || userInfo?.firstName || userInfo?.lastName || '-'}
            </Typography>
          </Box>
        </Stack>

        <Divider sx={{my: 2}} />

        <Stack direction={{xs: 'column', sm: 'row'}} spacing={{xs: 1.5, sm: 5}} flexWrap="wrap">
          <InfoBlock icon={<Icon path={mdiDoctor} size="24px" />} label="Il tuo medico" value={userInfo?.prescribingDoctor || '-'} />
          <InfoBlock icon={<Icon path={mdiDomain} size="24px" />} label="Centro medico" value={medicalCenter?.name || '-'} />
          <InfoBlock icon={<Icon path={mdiNavigation} size="24px" />} label="Indirizzo" value={medicalCenterFullAddress || '-'} href={medicalCenterMapsUrl} />
        </Stack>
      </Card>

      <Card title="I tuoi appuntamenti">
        {status === STATUS.PENDING ? (
          <Box sx={{display: 'flex', justifyContent: 'center', py: 4}}>
            <CircularProgress size={28} />
          </Box>
        ) : events.length === 0 ? (
          <EmptyState icon={<EventIcon fontSize="large" />} title="Nessun appuntamento" description="Non ci sono eventi da mostrare." />
        ) : (
          <Box sx={{maxWidth: {sm: 480}, mx: {sm: 'auto'}}}>
            {events.map((event, index) => {
              // The connecting line breaks right where the "Oggi" divider is inserted
              // (before/after this item) or at the very start/end of the list — same
              // split points as the legacy MUI Timeline, just drawn manually here.
              const hideConnectorAbove = index === 0 || index === appointmentIndexAfterToday + 1
              const hideConnectorBelow = index === events.length - 1 || index === appointmentIndexAfterToday

              // Avoid repeating the same date on consecutive same-day appointments — show
              // it only on the first row of each day (or right after the "Oggi" break).
              const previousEvent = events[index - 1]
              const isSameDayAsPrevious = Boolean(previousEvent) &&
                dateFormat.format(new Date(previousEvent.startDate)) === dateFormat.format(new Date(event.startDate)) &&
                yearFormat.format(new Date(previousEvent.startDate)) === yearFormat.format(new Date(event.startDate))
              const showDate = !isSameDayAsPrevious || hideConnectorAbove

              return (
                <React.Fragment key={`${event.startDate}-${index}`}>
                  {index === appointmentIndexAfterToday + 1 && <TodayDivider />}
                  <Box sx={{display: 'flex', gap: `${COL_GAP}px`}}>
                    <Box sx={{minWidth: DATE_COL_WIDTH, textAlign: 'right', py: `${ROW_PADDING_Y}px`}}>
                      {showDate && (
                        <>
                          <Typography variant="body2" sx={{fontWeight: 600, lineHeight: `${FIRST_LINE_HEIGHT}px`}}>
                            {dateFormat.format(new Date(event.startDate))}
                          </Typography>
                          <Typography variant="caption" sx={{color: NEUTRAL_SCALE[500]}}>
                            {yearFormat.format(new Date(event.startDate))}
                          </Typography>
                        </>
                      )}
                    </Box>
                    <Box sx={{display: 'flex', flexDirection: 'column', alignItems: 'center', width: DOT_SIZE, flexShrink: 0}}>
                      {/* Fixed-height top connector: puts the dot's center on the first text
                          line (date / title) instead of the vertical middle of the row. */}
                      <Box sx={{
                        height: ROW_PADDING_Y + (FIRST_LINE_HEIGHT - DOT_SIZE) / 2,
                        flexShrink: 0,
                        width: '2px',
                        backgroundColor: NEUTRAL_SCALE[200],
                        visibility: hideConnectorAbove ? 'hidden' : 'visible',
                      }}
                      />
                      <Box sx={{
                        width: DOT_SIZE,
                        height: DOT_SIZE,
                        borderRadius: '50%',
                        backgroundColor: theme.palette.primary.main,
                        flexShrink: 0,
                      }}
                      />
                      <Box sx={{
                        flex: 1,
                        width: '2px',
                        backgroundColor: NEUTRAL_SCALE[200],
                        visibility: hideConnectorBelow ? 'hidden' : 'visible',
                      }}
                      />
                    </Box>
                    <Box sx={{flex: 1, minWidth: 0, py: `${ROW_PADDING_Y}px`}}>
                      <Typography variant="body2" sx={{fontWeight: 600, lineHeight: `${FIRST_LINE_HEIGHT}px`}}>
                        {event.title}
                      </Typography>
                      <Typography variant="caption" sx={{color: NEUTRAL_SCALE[500]}}>
                        {`Ore ${timeFormat.format(new Date(event.startDate))}`}
                      </Typography>
                    </Box>
                  </Box>
                  {(index + 1 === events.length) && (events.length === appointmentIndexAfterToday + 1) && (
                    <TodayDivider />
                  )}
                </React.Fragment>
              )
            })}
          </Box>
        )}
      </Card>
    </Box>
  )
}
